import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import confetti from 'canvas-confetti';
import { Gift, Heart, RefreshCw, Sparkles, Star, X } from 'lucide-react';
import React from 'react';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
  React.useEffect(() => {
    if (isOpen) {
      // Trigger confetti when modal opens
      const duration = 2000;
      const end = Date.now() + duration;

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 45,
          spread: 30,
          origin: { x: 0 },
          colors: ['#FF5E5B', '#D8D8D8', '#20A4F3', '#FFB400', '#5DE3BD'],
        });

        confetti({
          particleCount: 3,
          angle: 135,
          spread: 30,
          origin: { x: 1 },
          colors: ['#FF5E5B', '#D8D8D8', '#20A4F3', '#FFB400', '#5DE3BD'],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
    }
  }, [isOpen]);

  return (
    <>
      <style>{mobileScrollStyle}</style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md w-[95vw] sm:w-[90vw] md:w-[450px] mx-auto bg-gray-900 shadow-lg rounded-2xl border-2 border-indigo-500/30 overflow-hidden p-0">
          <div className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 p-4 relative">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-yellow-400 animate-pulse" />
                <DialogTitle className="text-xl font-bold text-white">Change Log</DialogTitle>
                <Sparkles className="h-5 w-5 text-yellow-400 animate-pulse" />
              </div>
            </DialogHeader>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full hover:bg-white/10 h-8 w-8 p-0"
            >
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>

          {/* Content placeholder - will be filled in by user later */}
          <div className="p-5 overflow-y-auto custom-scrollbar sm:max-h-[60vh] max-h-[calc(100vh-240px)]">
            <div className="space-y-5 mobile-changelog">
              {/* V0.8.6 업데이트 */}
              <div className="relative pb-5 border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">1</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Gift className="h-3.5 w-3.5 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        V 0.8.6 UI/UX 개선 및 코드 품질 향상
                        <Heart className="h-3 w-3 text-pink-400" />
                      </h3>
                      <span className="text-xs text-gray-400 font-light">
                        {new Date()
                          .toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })
                          .replace(/\./g, '.')
                          .replace(/\s/g, '')}
                      </span>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>헤더 UI 개선</li>
                        <li>배경색 통일 및 일관성 개선</li>
                        <li>메뉴명 변경 (Daily Setlist)</li>
                        <li>검색 영역 공통화</li>
                        <li>달력 기능 추가</li>
                        <li>코드 품질 및 타입 안정성 개선</li>
                        <li>YouTube 바로가기 버튼 추가</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              {/* 업데이트 할 내용 */}
              <div className="relative pb-5 border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">2</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <RefreshCw className="h-3.5 w-3.5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        Update 기능 예정
                      </h3>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>YouTube API 제한 조절</li>
                        <li>플레이리스트 오류 수정</li>
                        <li>Spotify API 연동 예정</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative pb-5 border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">3</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Sparkles className="h-3.5 w-3.5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        로직 개선
                        <Heart className="h-3 w-3 text-pink-400" />
                      </h3>
                      <span className="text-xs text-gray-400 font-light">2025.04.14</span>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>전체 리스트 기능 제거</li>
                        <li>YouTube 플레이리스트 저장</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative pb-5 border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">2</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <RefreshCw className="h-3.5 w-3.5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        V0.8 UI 디자인 개선
                      </h3>
                      <span className="text-xs text-gray-400 font-light">2025.04.13</span>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>레이아웃 변경</li>
                        <li>연대기 페이지 생성</li>
                        <li>Ranking 페이지 생성</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative pb-5 border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">2</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <RefreshCw className="h-3.5 w-3.5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        UI 디자인 개선
                      </h3>
                      <span className="text-xs text-gray-400 font-light">2025.04.12</span>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>날짜별 플레이리스트 저장</li>
                        <li>아티스트 상세 페이지</li>
                        <li>Table 상세 보기</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative border-l-2 border-indigo-500/30 pl-5 ml-3">
                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full -left-[7px] flex items-center justify-center">
                  <span className="text-[8px] font-medium text-white">3</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Sparkles className="h-3.5 w-3.5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <h3 className="text-sm font-medium text-white flex items-center gap-1">
                        V 0.5 서비스 오픈
                        <Heart className="h-3 w-3 text-pink-400" />
                      </h3>
                      <span className="text-xs text-gray-400 font-light">2025.04.11</span>
                    </div>
                    <p className="text-xs font-light text-gray-300 leading-relaxed">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>iMBC 배철수 음악캠프 선곡표 수집</li>
                        <li>2006-01-01 ~ 2025-04-11</li>
                      </ul>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Add custom style for mobile changelog
const mobileScrollStyle = `
  @media (max-width: 640px) {
    .mobile-changelog > div:nth-child(n+3) {
      margin-bottom: 1rem;
    }
    .mobile-changelog {
      padding-bottom: 1rem;
    }
  }
`;

// Export using both methods for maximum compatibility
export { ChangelogModal };
export default ChangelogModal;
